import { useUserStore } from '@/stores/userStore';
import { initialize, type LDClient } from 'launchdarkly-js-client-sdk';
import { isEqual } from 'lodash-es';
import type { LDPluginOptions } from 'launchdarkly-vue-client-sdk';
import { getRoleStr, ROLES, type ApiV1, type ApiV2, type Role } from '@teamworksdev/influencer/core';
import { adaptUser, adaptUserProfile } from '@/utils/users';

export const ldConfig: LDPluginOptions = {
	context: {
		anonymous: true,
	},
	clientSideID: import.meta.env.VITE_LD_CLIENT_ID,
	options: {
		sendEventsOnlyForVariation: true,
	},
};

export interface LDUserContext {
	kind: 'multi';
	user: {
		key: string;
		name?: string;
		email?: string;
	};
	profile?: {
		key: string;
		profileType: 'compliance' | 'team' | 'brand' | 'unknown';
		name: string;
	};
	organization?: {
		key: string;
		name?: string;
		type: 'school' | 'team' | 'brand';
		[attribute: string]: any;
	};
}

function getProfileType(
	profile: ApiV1.FullUserProfile,
): 'team' | 'compliance' | 'brand' | 'unknown' {
	const map = new Map<Role, 'team' | 'compliance' | 'brand'>([
		[ROLES.BRAND_USER, 'brand'],
		[ROLES.COMPLIANCE_OFFICER, 'compliance'],
		[ROLES.TEAM_USER, 'team'],
	]);

	return map.get(profile.role_id) ?? 'unknown';
}

export function buildUserContext(
	user: ApiV2.User,
	profile?: ApiV1.FullUserProfile,
): LDUserContext {
	const context: LDUserContext = {
		kind: 'multi',
		user: {
			key: user.uuid,
			email: user.email,
			name: `${user.first_name} ${user.last_name}`,
		},
	};

	// Add profile context if profile is provided
	if (profile) {
		context.profile = {
			key: profile.uuid,
			profileType: getProfileType(profile),
			name: getRoleStr(profile.role_id),
		};

		// Add organization context based on profile type
		switch (profile.role_id) {
			case ROLES.COMPLIANCE_OFFICER:
				context.organization = {
					key: profile.data.school.uuid,
					name: profile.data.school.name,
					schoolId: profile.data.school.uuid,
					schoolName: profile.data.school.name,
					type: 'school',
				};
				break;
			case ROLES.TEAM_USER:
				context.organization = {
					key: profile.data.team.uuid || profile.data.school.uuid,
					name: profile.data.team.name || profile.data.school.name,
					type: profile.data.team.uuid ? 'team' : 'school',
					schoolId: profile.data.school.uuid,
					schoolName: profile.data.school.name,
				};
				break;
			case ROLES.BRAND_USER:
				context.organization = {
					key: profile.data.exchange_brand.uuid,
					name: profile.data.exchange_brand.name,
					type: 'brand',
				};
				break;
			default:
				break;
		}
	}

	return context;
}

let client: LDClient | undefined = undefined;
let context: LDUserContext | undefined = undefined;

export async function getClient() {
	if (client) return client;

	// Initialize new client
	client = initialize(
		import.meta.env.VITE_LD_CLIENT_ID,
		ldConfig.context!,
		ldConfig.options!,
	);
	await client.waitForInitialization(5);

	return client;
}

export async function updateLDContext() {
	const userStore = useUserStore();

	if (!userStore.user) {
		console.warn('[LaunchDarkly][getLDClient] User context missing');
		return await getClient();
	}

	const newContext = buildUserContext(
		adaptUser(userStore.user),
		userStore.profile
			? adaptUserProfile(userStore.profile)
			: undefined,
	);

	if (client && isEqual(context, newContext)) return client;

	context = newContext;

	if (client) {
		await client.identify(context, undefined, (err) => {
			if (err) {
				console.error('[LaunchDarkly][getLDClient] Error identifying user:', err);
				return;
			}
			return client;
		});
	}

	// Initialize new client
	client = initialize(
		import.meta.env.VITE_LD_CLIENT_ID,
		context,
		{
			sendEventsOnlyForVariation: true,
			streaming: true,
		},
	);
	await client.waitForInitialization(5);

	return client;
}
